import { VueConstructor } from 'vue';
import { InjectKey } from 'vue/types/options';
import { BackgroundTasksAPI } from './BackgroundTasksApiPolyfill';

type InjectArrayOptions<Injectant> = Array<keyof Injectant>;
type InjectRecordOptions<Injectant> = {
  [K in keyof Injectant]: InjectKey | { from?: InjectKey; default?: any };
};
type TypedInjectOptions<Injectant> = (
  | InjectArrayOptions<Injectant>
  | InjectRecordOptions<Injectant>
);

type Injected<T, K extends VueConstructor> = VueConstructor<InstanceType<K> & T>;

/**
 * A utility to provide typed injection to a Extended Vue
 * @param ctor
 * @param injectant
 */
export const inject = <T, K extends VueConstructor = VueConstructor>(
  ctor: K,
  injectant: TypedInjectOptions<T>,
): Injected<T, K> => (
    ctor.extend({
      inject: injectant as any,
    }) as Injected<T, K>
  )
;

type DynamicComponent<T> = () => Promise<T>;
type DynamicImportModule<T> = DynamicComponent<{ default: T }>;
type PrefetchOptions = Parameters<typeof BackgroundTasksAPI.requestIdleCallback>[1];

export const prefetchComponent = <T>(
  fetcher: DynamicImportModule<T>,
  options?: PrefetchOptions,
): DynamicComponent<T> => {
  const unwrappedFetcher: DynamicComponent<T> = () => fetcher().then(x => x.default);

  let p: Promise<T> | null = null;

  const idleCallbackHandle = BackgroundTasksAPI.requestIdleCallback(() => {
    p = unwrappedFetcher();
  }, options);

  return (): Promise<T> => {
    if (p) return p;

    BackgroundTasksAPI.cancelIdleCallback(idleCallbackHandle);
    return unwrappedFetcher();
  };
};
